import { onMounted, ref } from 'vue';
import { setRequestTypeHeader } from './axios-wrapper';
import { PARENT_APM_TOKEN_KEY, PARENT_AUTH_STORAGE_KEY } from '../constant/enums';
import { isAuthenticated } from './useIframeAuth';

/**
 * Host-app authentication (DreeSuite).
 *
 * DreeSuite reverse-proxies this app under its own domain — the same way it serves
 * `/sales-plat/preview` — so both share a browser origin and DreeSuite's
 * `localStorage` is readable here directly. No postMessage/authorization-code
 * handshake is needed: the APM token is owned and rotated by DreeSuite, and we only
 * read it and forward it as a bearer token. The SalesPlat backend validates it on
 * every call against its own token-validator endpoint.
 *
 * The APM broker flow in `useIframeAuth` remains the default for every other host.
 */

const TOKEN_WAIT_TIMEOUT_MS = 10000;
const TOKEN_POLL_INTERVAL_MS = 250;

// Accepts either a full origin ("https://host", matching the VUE_APP_TRUSTED_ORIGINS
// convention) or a bare hostname, since the comparison below is against
// `location.hostname` which carries neither scheme nor port.
const toHostname = (originOrHost: string): string =>
  originOrHost.trim().replace(/^[a-z]+:\/\//i, '').split('/')[0].split(':')[0];

// Entries as authored in the env, scheme preserved. Some environments configure a
// bare hostname (e.g. "local.dreeshomes.com"), so neither form can be assumed.
const parentAppOrigins: string[] = (() => {
  const raw = ((process.env as any).VUE_APP_PARENT_APP_ORIGINS as string) || '';
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((entry: string) => entry.trim()).filter(Boolean);
    }
  } catch {
    // Fall through to the comma-separated form.
  }
  return raw.split(',').map((entry) => entry.trim()).filter(Boolean);
})();

const parentAppHosts: string[] = parentAppOrigins.map(toHostname).filter(Boolean);

/**
 * True when this app is being served from a configured host application's domain.
 * `window.location.hostname` is not attacker-controllable from inside the page; an
 * unrecognised host simply falls through to the APM broker flow.
 */
export const isParentAppHost = (): boolean => parentAppHosts.includes(window.location.hostname);

/**
 * The host application's origin to hand the browser back to when its token is
 * rejected. Prefers the configured entry matching the host we are served from, so a
 * multi-entry env cannot send the user to a different environment's host.
 * Scheme-less entries inherit the current page's protocol.
 */
export const resolveParentAppOrigin = (): string => {
  const configuredEntry =
    parentAppOrigins.find((entry) => toHostname(entry) === window.location.hostname) ??
    parentAppOrigins[0];

  if (!configuredEntry) return window.location.origin;

  const withoutTrailingSlash = configuredEntry.replace(/\/$/, '');
  return /^[a-z]+:\/\//i.test(withoutTrailingSlash)
    ? withoutTrailingSlash
    : `${window.location.protocol}//${withoutTrailingSlash}`;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Reads the APM token out of DreeSuite's stored auth context.
 * Returns null (never throws) so a missing or malformed entry cannot reject every
 * outgoing request from the Axios interceptor.
 */
export const readApmToken = (): string | null => {
  let authContext: any;
  try {
    const storedAuthEntry = localStorage.getItem(PARENT_AUTH_STORAGE_KEY);
    if (!storedAuthEntry) return null;

    authContext = JSON.parse(storedAuthEntry);
    // DreeSuite may store the context double-encoded.
    if (typeof authContext === 'string') {
      authContext = JSON.parse(authContext);
    }
  } catch (parseError) {
    console.error(
      `Platmap: failed to parse localStorage["${PARENT_AUTH_STORAGE_KEY}"] as JSON`,
      parseError
    );
    return null;
  }

  const token = authContext?.[PARENT_APM_TOKEN_KEY];
  if (isNonEmptyString(token)) return token;

  // Log the shape only — never the stored values.
  console.error(
    `Platmap: "${PARENT_APM_TOKEN_KEY}" not found in localStorage["${PARENT_AUTH_STORAGE_KEY}"]`,
    { keysPresent: authContext ? Object.keys(authContext) : [] }
  );
  return null;
};

/**
 * Resolves as soon as the APM token is readable.
 *
 * When framed, DreeSuite may write its auth context after this iframe mounts, so we
 * also listen for the same-origin `storage` event and poll as a fallback (the event
 * does not fire for writes that happened before the listener was attached). When not
 * framed there is no live DreeSuite context to write the token later, so a missing
 * token is final and we fail immediately rather than waiting out the timeout.
 */
const waitForApmToken = (): Promise<string> =>
  new Promise((resolve, reject) => {
    const tokenOnMount = readApmToken();
    if (tokenOnMount) {
      resolve(tokenOnMount);
      return;
    }

    if (window.parent === window) {
      reject(new Error('No DreeSuite APM token present and no host frame to supply one'));
      return;
    }

    let pollTimer: number;
    let timeoutTimer: number;

    const stopWatching = () => {
      window.removeEventListener('storage', onStorageChange);
      window.clearInterval(pollTimer);
      window.clearTimeout(timeoutTimer);
    };

    const settleIfTokenAvailable = (): boolean => {
      const token = readApmToken();
      if (!token) return false;
      stopWatching();
      resolve(token);
      return true;
    };

    function onStorageChange(event: StorageEvent) {
      if (event.key === PARENT_AUTH_STORAGE_KEY) settleIfTokenAvailable();
    }

    window.addEventListener('storage', onStorageChange);
    pollTimer = window.setInterval(settleIfTokenAvailable, TOKEN_POLL_INTERVAL_MS);
    timeoutTimer = window.setTimeout(() => {
      stopWatching();
      reject(new Error('Timed out waiting for the DreeSuite APM token'));
    }, TOKEN_WAIT_TIMEOUT_MS);
  });

/**
 * Drop-in alternative to `useIframeAuth` for DreeSuite-hosted deployments. Returns
 * the same shape so `Root.vue` and its children need no awareness of which auth path
 * is active.
 */
export function useParentAppToken(mode: string) {
  const jwt = ref<string | null>(null);
  // DreeSuite owns token rotation, so there is no re-auth round trip to show.
  const isReauthenticating = ref(false);
  const error = ref<string | null>(null);

  let resolveReady: () => void;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });

  onMounted(async () => {
    setRequestTypeHeader(mode);
    try {
      jwt.value = await waitForApmToken();
      isAuthenticated.value = true;
    } catch (tokenError: any) {
      error.value = 'Your session could not be verified. Please reopen this page from DreeSuite.';
      console.error('Platmap: DreeSuite authentication failed', tokenError?.message);
    } finally {
      // Resolve on failure too, so data fetching gated on `ready` cannot hang.
      resolveReady?.();
    }
  });

  return {
    jwt,
    isAuthenticated,
    isReauthenticating,
    error,
    ready,
    sendReauthMessage: () => undefined,
  };
}
